// date — native date input, themed to match the outlined text fields.
//
// Deliberately native rather than a picker library: it is the only control
// that gets the correct locale, keyboard and accessibility behaviour on UAE
// mobile browsers for free, and it keeps the bundle out of a third picker dep.
import React from "react";
import { TextField } from "@mui/material";
import FieldFrame from "./FieldFrame";

// Schemas express bounds relatively so a config never goes stale:
//   minDate: "today" | "-18y" | "+1d" | "2020-01-01"
const resolveBound = (spec) => {
  if (!spec) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(spec)) return spec;
  const now = new Date();
  if (spec === "today") return now.toISOString().slice(0, 10);
  const match = /^([+-])(\d+)([dmy])$/.exec(spec);
  if (!match) return undefined;
  const [, sign, amount, unit] = match;
  const n = Number(amount) * (sign === "-" ? -1 : 1);
  if (unit === "d") now.setDate(now.getDate() + n);
  if (unit === "m") now.setMonth(now.getMonth() + n);
  if (unit === "y") now.setFullYear(now.getFullYear() + n);
  return now.toISOString().slice(0, 10);
};

// The browser only accepts YYYY-MM-DD. Values arriving from MySQL are ISO
// timestamps and values from OCR can be DD/MM/YYYY, so normalise both.
const toInputValue = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(String(value));
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
};

const DateInput = ({ field, value, error, disabled, onChange, onBlur }) => {
  const id = `f-${field.name}`;
  return (
    <FieldFrame
      label={field.label}
      required={field.required}
      help={field.help}
      error={error}
      htmlFor={id}
    >
      <TextField
        id={id}
        fullWidth
        size="small"
        type="date"
        variant="outlined"
        name={field.name}
        value={toInputValue(value)}
        disabled={disabled}
        error={!!error}
        onChange={onChange}
        onBlur={onBlur}
        inputProps={{
          min: resolveBound(field.props?.minDate),
          max: resolveBound(field.props?.maxDate),
          "aria-invalid": !!error,
        }}
        sx={{
          // Make the whole field open the picker, not just the tiny icon.
          "& input::-webkit-calendar-picker-indicator": { cursor: "pointer" },
        }}
      />
    </FieldFrame>
  );
};

export { toInputValue };
export default DateInput;
